/**
 * useSliceState — slice state + localStorage persistence per setup.
 *
 * Each setup gets its own slice memory. When you slice the "5M 05 EMA"
 * page to "Opening Drive × VIX 21-25 × Long", that exact slice reapplies
 * the next time you open that setup — without polluting your other
 * setups' views.
 *
 * Why per-setup: a slice tailored to one setup ("VIX 21-25 × Opening Drive")
 * is rarely meaningful for another setup. Per-setup slice memory matches
 * the way you actually use the page.
 *
 * State shape mirrors `EMPTY_SLICE` from sliceFilters.js so consumers can
 * pass the value straight into `filterTradesBySlice(trades, slice, opts)`.
 *
 * Persistence:
 *   - localStorage key: `journifi.sliceState.{setupName}`
 *   - Written on every change (debounce-free — slice changes are user-driven,
 *     not continuous; no thrash)
 *   - Read once on setup change (mount + setupName dep)
 *
 * Identity stability:
 *   - The returned `slice` is stable across renders when the underlying
 *     values haven't changed. This is critical because consumers pipe `slice`
 *     into useMemo deps for expensive analytics computations.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EMPTY_SLICE, hasAnyActiveDimension } from '../utils/sliceFilters';

const STORAGE_PREFIX = 'journifi.sliceState';

const storageKey = (setupName) => `${STORAGE_PREFIX}.${setupName || '__default__'}`;

const safeReadSlice = (setupName) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(setupName));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Defensive shape coercion — older saved slices might miss fields
    return normalizeSlice(parsed);
  } catch {
    return null;
  }
};

const safeWriteSlice = (setupName, slice) => {
  if (typeof window === 'undefined') return;
  try {
    // Don't persist the empty slice — saves localStorage entries and avoids
    // confusing "remembered nothing" on next open. We just remove the key
    // when the slice is empty; the read returns null which initializes to
    // EMPTY_SLICE anyway.
    if (!hasAnyActiveDimension(slice)) {
      window.localStorage.removeItem(storageKey(setupName));
      return;
    }
    window.localStorage.setItem(storageKey(setupName), JSON.stringify(slice));
  } catch {
    // Quota exceeded or private mode — silently skip
  }
};

/**
 * Coerce arbitrary input into a valid slice shape. Guards against:
 *   - Older saved slices missing fields added later
 *   - User-tampered localStorage values
 *   - Future-version-wrote-a-newer-shape backwards reads
 */
const normalizeSlice = (raw) => {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_SLICE };
  return {
    time:      Array.isArray(raw.time)      ? raw.time.filter(v => typeof v === 'string')      : [],
    vix:       Array.isArray(raw.vix)       ? raw.vix.filter(v => typeof v === 'string')       : [],
    direction: Array.isArray(raw.direction) ? raw.direction.filter(v => typeof v === 'string') : [],
    dayOfWeek: Array.isArray(raw.dayOfWeek) ? raw.dayOfWeek.filter(v => typeof v === 'string') : [],
    range:     typeof raw.range === 'string' ? raw.range : 'all',
  };
};

/**
 * @param {string} setupName  Slice memory is keyed by setup. Pass the active
 *                            setup name. Falsy → __default__ key.
 * @returns {Object}
 *   {
 *     slice,                       // current slice (stable reference)
 *     setDimension(dim, values),   // replace values for one dim (array)
 *     toggleValue(dim, value),     // flip a single value's presence
 *     setRange(presetId),          // set the date-range preset
 *     clearDimension(dim),         // wipe one dim
 *     clearAll(),                  // reset to EMPTY_SLICE
 *     setSlice(newSlice),          // overwrite whole slice (used by "saved slices" later)
 *     isAnyActive,                 // bool — slice has any active dim
 *   }
 */
export const useSliceState = (setupName) => {
  // Lazy-initialize from localStorage so the first paint already has the
  // restored slice. Without lazy init, we'd render once with EMPTY_SLICE
  // then re-render after the effect — a visible flicker on big trade sets.
  const [slice, setSliceState] = useState(() => safeReadSlice(setupName) || { ...EMPTY_SLICE });

  // When setupName changes (user switches setups), reload the slice from
  // storage for the new setup. Without this, slice memory would leak
  // across setups.
  const prevSetupRef = useRef(setupName);
  useEffect(() => {
    if (prevSetupRef.current !== setupName) {
      prevSetupRef.current = setupName;
      setSliceState(safeReadSlice(setupName) || { ...EMPTY_SLICE });
    }
  }, [setupName]);

  // Persist on every change. Skips no-op writes via the empty-slice removal.
  useEffect(() => {
    safeWriteSlice(setupName, slice);
  }, [setupName, slice]);

  // ── Actions ──

  const setDimension = useCallback((dim, values) => {
    setSliceState(prev => {
      const next = { ...prev };
      next[dim] = Array.isArray(values) ? values : [];
      return next;
    });
  }, []);

  const toggleValue = useCallback((dim, value) => {
    setSliceState(prev => {
      const current = Array.isArray(prev[dim]) ? prev[dim] : [];
      const has = current.includes(value);
      const next = { ...prev };
      next[dim] = has ? current.filter(v => v !== value) : [...current, value];
      return next;
    });
  }, []);

  const setRange = useCallback((presetId) => {
    setSliceState(prev => ({ ...prev, range: presetId || 'all' }));
  }, []);

  const clearDimension = useCallback((dim) => {
    setSliceState(prev => {
      const next = { ...prev };
      if (dim === 'range') next.range = 'all';
      else next[dim] = [];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setSliceState({ ...EMPTY_SLICE });
  }, []);

  const overwriteSlice = useCallback((newSlice) => {
    setSliceState(normalizeSlice(newSlice));
  }, []);

  // Memoize the active-flag so consumers don't re-evaluate on every render
  const isAnyActive = useMemo(() => hasAnyActiveDimension(slice), [slice]);

  return {
    slice,
    setDimension,
    toggleValue,
    setRange,
    clearDimension,
    clearAll,
    setSlice: overwriteSlice,
    isAnyActive,
  };
};

export default useSliceState;
